import React, { useState } from 'react';
import payments from '../../../data/payments';
import YouTubeRow from './YouTubeRow';
import './style.css';
import useStyles from '../../../common/CustomStyles';
import CustomTextField from '../../../common/CustomTextField';
import Loader from '../../../common/Loader';
import Button from '@material-ui/core/Button';
import Toast from '../../../common/Alerts/Toast';
import { getI18N } from '../../../i18N';

export default function PhonePe({ gatewayData }) {
  const classes = useStyles();
  const { t } = getI18N();

  const savedCredentials = gatewayData.credentials || {};
  const savedVersion = savedCredentials.clientId ? 'v2' : savedCredentials.salt_index ? 'v1' : null;

  const [isActive, setIsActive] = useState(gatewayData.isActive || false);
  const [isV2Saved, setIsV2Saved] = useState(savedVersion === 'v2');
  const [activeTab, setActiveTab] = useState(savedVersion === 'v1' ? 'v1' : 'v2');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [v2Credentials, setV2Credentials] = useState({
    mid: savedVersion === 'v2' ? savedCredentials.mid || '' : '',
    client_id: savedVersion === 'v2' ? savedCredentials.clientId || '' : '',
    client_secret: savedVersion === 'v2' ? savedCredentials.clientSecret || '' : '',
  });

  const [toastState, setToastState] = useState({ message: '', open: false });

  const showTabSwitcher = savedVersion === 'v1' && !isV2Saved;
  const showDeprecationBanner = savedVersion === 'v1' && !isV2Saved;

  const onChange = (e) => {
    const { name, value } = e.target;
    setV2Credentials((prev) => ({ ...prev, [name]: value }));
    setErrors({});
  };

  const validateV2 = () => {
    const _errors = {};
    Object.keys(v2Credentials).forEach((key) => {
      if (!v2Credentials[key]) {
        _errors[key] = t('this_field_cannot_be_left_blank');
      }
    });
    setErrors(_errors);
    return Object.keys(_errors).length;
  };

  const onSubmitV2 = () => {
    if (validateV2()) return;
    setLoading(true);
    const payload = {
      mid: v2Credentials.mid,
      clientId: v2Credentials.client_id,
      clientSecret: v2Credentials.client_secret,
    };
    payments
      .enablePhonePe(payload)
      .then(() => {
        window.location.reload();
      })
      .catch((err) => {
        let message = t('an_error_occured');
        if (err && err.response && err.response.data && err.response.data.message) {
          message = err.response.data.message;
        }
        setLoading(false);
        setToastState({ message, open: true });
      });
  };

  const onEnableV1 = () => {
    setLoading(true);
    payments
      .enablePhonePe({
        mid: savedCredentials.mid,
        salt_key: savedCredentials.salt_key,
        salt_index: savedCredentials.salt_index,
      })
      .then(() => {
        setIsActive(true);
        setLoading(false);
        setToastState({ message: t('payment_gateway_enabled'), open: true });
      })
      .catch((err) => {
        let message = t('an_error_occured');
        if (err && err.response && err.response.data && err.response.data.message) {
          message = err.response.data.message;
        }
        setLoading(false);
        setToastState({ message, open: true });
      });
  };

  const onDisable = () => {
    setLoading(true);
    payments
      .disablePhonePe()
      .then(() => {
        setIsActive(false);
        setLoading(false);
        setToastState({ message: t('payment_gateway_disabled'), open: true });
      })
      .catch((err) => {
        let message = t('an_error_occured');
        if (err && err.response && err.response.data && err.response.data.message) {
          message = err.response.data.message;
        }
        setLoading(false);
        setToastState({ message, open: true });
      });
  };

  return (
    <div className="PhonePeContainer">
      <div className="introContainer">
        <div className="introText">{t('phonepe_v2_help_text')}</div>
        {gatewayData.youtubeTutorialVideoId && (
          <YouTubeRow videoId={gatewayData.youtubeTutorialVideoId} />
        )}
      </div>

      {showDeprecationBanner && (
        <div className="PhonePeDeprecationBanner">
          <div className="PhonePeDeprecationBannerTitle">{t('phonepe_v1_deprecation_title')}</div>
          <div className="PhonePeDeprecationBannerDesc">{t('phonepe_v1_deprecation_desc')}</div>
        </div>
      )}

      {showTabSwitcher && (
        <div className="PhonePeTabRow">
          <button
            className={`PhonePeTab${activeTab === 'v2' ? ' active' : ''}`}
            onClick={() => setActiveTab('v2')}
          >
            V2 – OAuth
          </button>
          <button
            className={`PhonePeTab${activeTab === 'v1' ? ' active' : ''}`}
            onClick={() => setActiveTab('v1')}
          >
            V1 – Legacy
          </button>
        </div>
      )}

      <form className={classes.root} noValidate autoComplete="off">
        {activeTab === 'v2' && (
          <>
            <CustomTextField
              fullWidth
              label={t('merchant_id')}
              margin="normal"
              value={v2Credentials.mid}
              name="mid"
              onChange={onChange}
              error={!!errors.mid}
              helperText={errors.mid}
              disabled={isV2Saved}
            />
            <CustomTextField
              fullWidth
              label={t('phonepe_client_id')}
              value={v2Credentials.client_id}
              name="client_id"
              onChange={onChange}
              error={!!errors.client_id}
              helperText={errors.client_id}
              disabled={isV2Saved}
            />
            <CustomTextField
              fullWidth
              label={t('phonepe_client_secret')}
              type="password"
              value={v2Credentials.client_secret}
              name="client_secret"
              onChange={onChange}
              error={!!errors.client_secret}
              helperText={errors.client_secret}
              disabled={isV2Saved}
            />
          </>
        )}

        {activeTab === 'v1' && savedVersion === 'v1' && (
          <>
            <CustomTextField
              fullWidth
              label={t('merchant_id')}
              margin="normal"
              value={savedCredentials.mid || ''}
              name="mid"
              disabled
            />
            <CustomTextField
              fullWidth
              label="Salt Key"
              type="password"
              value={savedCredentials.salt_key || ''}
              name="salt_key"
              disabled
            />
            <CustomTextField
              fullWidth
              label="Salt Index"
              value={String(savedCredentials.salt_index || '')}
              name="salt_index"
              disabled
            />
          </>
        )}

        {loading ? (
          <div style={{ marginLeft: 24, marginTop: 24 }}>
            <Loader small />
          </div>
        ) : activeTab === 'v1' ? (
          <div style={{ display: 'flex', gap: 12 }}>
            {isActive ? (
              <Button
                variant="contained"
                size="large"
                className={classes.buttonDisable}
                onClick={onDisable}
              >
                {t('disable')}
              </Button>
            ) : (
              <Button
                variant="contained"
                size="large"
                className={classes.button}
                style={{ marginLeft: '16px' }}
                onClick={onEnableV1}
              >
                {t('enable')}
              </Button>
            )}
            <Button
              variant="contained"
              size="large"
              className={classes.button}
              onClick={() => setActiveTab('v2')}
            >
              Migrate to V2 →
            </Button>
          </div>
        ) : isActive && isV2Saved ? (
          <Button
            variant="contained"
            size="large"
            className={classes.buttonDisable}
            onClick={onDisable}
          >
            {t('disable')}
          </Button>
        ) : (
          <Button
            variant="contained"
            size="large"
            className={classes.button}
            style={{ marginLeft: 24 }}
            onClick={onSubmitV2}
          >
            {t('enable')}
          </Button>
        )}
      </form>

      <Toast
        open={toastState.open}
        message={toastState.message}
        onClose={() => setToastState({ open: false, message: '' })}
      />
    </div>
  );
}
